/**
 * Fase 29 · Calendario compartido con eventos manuales + publicaciones/correos.
 *
 * Muestra en una grilla mensual:
 *  - Eventos manuales de la empresa (creables por cualquier miembro)
 *  - Publicaciones sociales programadas/publicadas (read-only, icono)
 *  - Campañas de email programadas/enviadas (read-only, icono)
 *
 * Colores: cada usuario tiene un color estable (hash del user_id → paleta).
 * Recurrencia: none/daily/weekly/monthly con fecha de fin opcional.
 * GMT-4 en todos los timestamps.
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime, formatTime, formatDate } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, RefreshCw,
  Loader2, Trash2, Instagram, Facebook, Music2, MessageCircle, Mail, Users,
} from "lucide-react";
import { toast } from "sonner";

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

// Paleta estable para asignar color por usuario (hash mod paleta).
const USER_PALETTE = [
  "#00A36C", "#FFD700", "#E1306C", "#1877F2", "#8B5CF6",
  "#F59E0B", "#EF4444", "#0EA5E9", "#22C55E", "#EC4899",
];
function colorForUser(userId) {
  if (!userId) return "#57534E";
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return USER_PALETTE[hash % USER_PALETTE.length];
}

function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const dayOfWeek = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - dayOfWeek);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function sameDayGmt4(iso, jsDate) {
  if (!iso || !jsDate) return false;
  // Ajustar iso a GMT-4 y comparar con jsDate (local del navegador).
  try {
    const t = new Date(iso);
    // Convertir a GMT-4 sin importar TZ del navegador: usar UTC + offset.
    const offset = -4 * 60;
    const gmt4 = new Date(t.getTime() + (offset - t.getTimezoneOffset()) * 60 * 1000);
    return gmt4.getFullYear() === jsDate.getFullYear()
      && gmt4.getMonth() === jsDate.getMonth()
      && gmt4.getDate() === jsDate.getDate();
  } catch { return false; }
}

const PLATFORM_ICONS = {
  instagram: Instagram, facebook: Facebook, tiktok: Music2,
  whatsapp: MessageCircle, email: Mail,
};

export default function Calendario() {
  const { user } = useAuth();
  const [cursor, setCursor] = useState(new Date());
  const [manualEvents, setManualEvents] = useState([]);
  const [posts, setPosts] = useState([]); // events kind=post|campaign
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const days = useMemo(() => monthGrid(year, month), [year, month]);

  const rangeStart = useMemo(() => new Date(year, month - 1, 1).toISOString(), [year, month]);
  const rangeEnd = useMemo(() => new Date(year, month + 2, 0, 23, 59).toISOString(), [year, month]);

  const load = async () => {
    setLoading(true);
    try {
      const [m, p, mm] = await Promise.all([
        api.get(`/calendar/manual-events?start=${rangeStart}&end=${rangeEnd}`),
        api.get(`/calendar/events?start=${rangeStart}&end=${rangeEnd}`).catch(() => ({ data: [] })),
        api.get("/team/company-members").catch(() => ({ data: [] })),
      ]);
      setManualEvents(m.data || []);
      setPosts(p.data || []);
      setMembers(mm.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar calendario");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rangeStart]);

  const openCreate = (jsDate) => {
    const iso = jsDate ? new Date(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate(), 9).toISOString() : "";
    setEditing({
      title: "", description: "",
      start_at: iso.slice(0, 16),
      recurrence: "none", recurrence_until: "",
      assign_all_company: true, assignees: [],
    });
    setDialogOpen(true);
  };

  const openEdit = (evt) => {
    setEditing({
      id: evt.id,
      title: evt.title,
      description: evt.description || "",
      start_at: (evt.start_at || "").slice(0, 16),
      recurrence: evt.recurrence || "none",
      recurrence_until: (evt.recurrence_until || "").slice(0, 16),
      assign_all_company: !!evt.assign_all_company,
      assignees: evt.assignees || [],
      created_by: evt.created_by,
    });
    setDetail(null);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing.title.trim() || !editing.start_at) {
      toast.error("Título y fecha son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        description: editing.description || null,
        start_at: new Date(editing.start_at).toISOString(),
        recurrence: editing.recurrence,
        recurrence_until: editing.recurrence_until ? new Date(editing.recurrence_until).toISOString() : null,
        assign_all_company: !!editing.assign_all_company,
        assignees: editing.assign_all_company ? [] : (editing.assignees || []),
      };
      if (editing.id) {
        await api.patch(`/calendar/manual-events/${editing.id}`, payload);
        toast.success("Evento actualizado");
      } else {
        await api.post("/calendar/manual-events", payload);
        toast.success("Evento creado — se notificó a los asignados");
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  const remove = async (evtId) => {
    if (!window.confirm("¿Eliminar este evento? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/calendar/manual-events/${evtId}`);
      toast.success("Evento eliminado");
      setDetail(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  // Índice de eventos por día (GMT-4)
  const eventsByDay = useMemo(() => {
    const map = new Map();
    days.forEach((d) => map.set(d.toDateString(), { manual: [], external: [] }));
    manualEvents.forEach((ev) => {
      days.forEach((d) => {
        if (sameDayGmt4(ev.start_at, d)) {
          const key = d.toDateString();
          if (map.has(key)) map.get(key).manual.push(ev);
        }
      });
    });
    posts.forEach((p) => {
      days.forEach((d) => {
        if (sameDayGmt4(p.at, d)) {
          const key = d.toDateString();
          if (map.has(key)) map.get(key).external.push(p);
        }
      });
    });
    return map;
  }, [manualEvents, posts, days]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <CalendarIcon className="h-8 w-8 text-brand-emerald" />
            Calendario compartido
            <Badge className="bg-brand-gold-soft text-brand-gold-dark border border-brand-gold/40 text-[10px]">
              GMT-4
            </Badge>
          </h1>
          <p className="text-sm text-stone-500 mt-2 max-w-2xl">
            Coordina reuniones, entregas y publicaciones del equipo. Cada miembro tiene un color propio,
            y las publicaciones/correos programados aparecen como referencia.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="calendar-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Recargar
          </Button>
          <Button className="btn-primary" onClick={() => openCreate(new Date())} data-testid="calendar-new-event">
            <Plus className="h-4 w-4 mr-1" /> Nuevo evento
          </Button>
        </div>
      </div>

      {/* Month nav */}
      <div className="card-elevated p-4 flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="font-serif-display text-2xl text-brand-ink">
          {MONTH_NAMES[month]} <span className="text-stone-500">{year}</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Legend por miembro */}
      {members.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs uppercase tracking-widest font-semibold text-stone-500 mr-2">Miembros:</div>
          {members.slice(0, 12).map((m) => (
            <div key={m.user_id} className="flex items-center gap-1.5 text-xs">
              <span className="inline-block h-3 w-3 rounded-full border border-stone-200" style={{ background: colorForUser(m.user_id) }} />
              <span className="text-stone-700">{m.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="card-elevated overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border">
          {WEEKDAYS.map((w) => (
            <div key={w} className="p-2 text-xs uppercase tracking-widest font-semibold text-stone-500 text-center border-r border-border last:border-r-0">
              {w}
            </div>
          ))}
        </div>
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-brand-emerald" /></div>
        ) : (
          <div className="grid grid-cols-7">
            {days.map((d, i) => {
              const key = d.toDateString();
              const bucket = eventsByDay.get(key) || { manual: [], external: [] };
              const inMonth = d.getMonth() === month;
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <div
                  key={i}
                  className={`min-h-[110px] border-r border-b border-border last:border-r-0 p-1.5 relative group ${inMonth ? "bg-white" : "bg-stone-50/50"} ${isToday ? "ring-2 ring-brand-gold ring-inset" : ""}`}
                  data-testid={`cal-day-${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`}
                >
                  <div className={`text-xs font-semibold mb-1 flex items-center justify-between ${inMonth ? "text-brand-ink" : "text-stone-400"}`}>
                    <span>{d.getDate()}</span>
                    <button
                      onClick={() => openCreate(d)}
                      className="opacity-0 group-hover:opacity-100 transition text-brand-emerald hover:text-brand-emerald-dark"
                      data-testid={`cal-add-${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`}
                      title="Agregar evento"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {bucket.manual.slice(0, 3).map((ev) => (
                      <button
                        key={ev.occurrence_id || ev.id}
                        onClick={() => setDetail(ev)}
                        className="w-full text-left text-[10px] rounded px-1.5 py-1 truncate hover:opacity-80 transition"
                        style={{
                          background: `${colorForUser(ev.created_by)}22`,
                          borderLeft: `3px solid ${colorForUser(ev.created_by)}`,
                        }}
                        data-testid={`cal-event-${ev.occurrence_id || ev.id}`}
                      >
                        <span className="font-semibold">{formatTime(ev.start_at)}</span>{" "}
                        <span>{ev.title}</span>
                      </button>
                    ))}
                    {bucket.external.slice(0, 2).map((p) => {
                      const Icon = PLATFORM_ICONS[p.platform] || Mail;
                      return (
                        <div
                          key={p.id}
                          className="text-[10px] rounded px-1.5 py-1 flex items-center gap-1 bg-stone-100 text-stone-600 truncate"
                          data-testid={`cal-external-${p.id}`}
                          title={`${p.kind === "post" ? "Publicación" : "Campaña"}: ${p.title}`}
                        >
                          <Icon className="h-3 w-3 shrink-0" />
                          <span className="truncate">{formatTime(p.at)} · {p.title || "—"}</span>
                        </div>
                      );
                    })}
                    {(bucket.manual.length + bucket.external.length) > 5 && (
                      <div className="text-[9px] text-stone-400">
                        +{bucket.manual.length + bucket.external.length - 5} más
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog create/edit */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-lg" data-testid="cal-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing?.id ? "Editar evento" : "Nuevo evento"}
            </DialogTitle>
            <DialogDescription>
              Los asignados recibirán notificación al crearse y un recordatorio 15 min antes.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Título</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid="cal-input-title"
                  placeholder="Reunión semanal, entrega, campaña..."
                />
              </div>
              <div className="space-y-1">
                <Label>Descripción (opcional)</Label>
                <Textarea
                  rows={2}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  data-testid="cal-input-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Fecha/hora (GMT-4)</Label>
                  <Input
                    type="datetime-local"
                    value={editing.start_at}
                    onChange={(e) => setEditing({ ...editing, start_at: e.target.value })}
                    data-testid="cal-input-start"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Recurrencia</Label>
                  <Select
                    value={editing.recurrence}
                    onValueChange={(v) => setEditing({ ...editing, recurrence: v })}
                  >
                    <SelectTrigger data-testid="cal-input-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguna</SelectItem>
                      <SelectItem value="daily">Diaria</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editing.recurrence !== "none" && (
                <div className="space-y-1">
                  <Label>Fecha fin de recurrencia (opcional)</Label>
                  <Input
                    type="datetime-local"
                    value={editing.recurrence_until}
                    onChange={(e) => setEditing({ ...editing, recurrence_until: e.target.value })}
                    data-testid="cal-input-until"
                  />
                </div>
              )}

              <div className="space-y-2 border-t pt-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={!!editing.assign_all_company}
                    onCheckedChange={(v) => setEditing({ ...editing, assign_all_company: v })}
                    data-testid="cal-input-assign-all"
                  />
                  <Users className="h-4 w-4 text-brand-emerald" />
                  Asignar a toda la empresa
                </label>
                {!editing.assign_all_company && (
                  <div className="max-h-40 overflow-y-auto border border-border rounded p-2 space-y-1">
                    {members.map((m) => (
                      <label key={m.user_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-stone-50 px-1 rounded">
                        <input
                          type="checkbox"
                          checked={editing.assignees?.includes(m.user_id)}
                          onChange={(e) => {
                            const set = new Set(editing.assignees || []);
                            if (e.target.checked) set.add(m.user_id); else set.delete(m.user_id);
                            setEditing({ ...editing, assignees: Array.from(set) });
                          }}
                          data-testid={`cal-input-assignee-${m.user_id}`}
                        />
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: colorForUser(m.user_id) }} />
                        <span>{m.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }} disabled={saving}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="btn-primary" data-testid="cal-save-event">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing?.id ? "Guardar cambios" : "Crear evento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-md" data-testid="cal-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">{detail?.title}</DialogTitle>
            <DialogDescription>
              {detail && formatDateTime(detail.start_at)}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              {detail.description && (
                <div className="whitespace-pre-wrap text-brand-ink">{detail.description}</div>
              )}
              <div className="flex items-center gap-2 text-xs text-stone-500">
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: colorForUser(detail.created_by) }} />
                Creado por <strong>{detail.created_by_name}</strong>
              </div>
              {detail.recurrence && detail.recurrence !== "none" && (
                <Badge className="bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40 text-[10px]">
                  Repite: {detail.recurrence}
                </Badge>
              )}
              {detail.assign_all_company ? (
                <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30 text-[10px]">
                  Toda la empresa
                </Badge>
              ) : (
                <div className="text-xs text-stone-500">
                  Asignados: {(detail.assignees || []).length}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {detail && (user?.role === "master" || user?.role === "admin" || detail.created_by === user?.id) && (
              <>
                <Button variant="outline" onClick={() => openEdit(detail)} data-testid="cal-edit-btn">
                  Editar
                </Button>
                <Button
                  variant="outline"
                  className="text-destructive hover:bg-destructive/10"
                  onClick={() => remove(detail.id)}
                  data-testid="cal-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
